/**
 * /offline — Network-failure branded surface.
 *
 * Round 5 Item 7 Tier 1 #4. Rendered when `navigator.onLine === false`
 * OR when the OfflineDetector (mounted by App.tsx) detects sustained
 * fetch failures consistent with a network outage (vs a single
 * server-side hiccup).
 *
 * The OfflineDetector polls online/offline events on window + listens for
 * the custom `olytic:network-offline` event that ApiError.network_failure
 * can fire. When offline is detected, the detector redirects here. When
 * `navigator.onLine` flips back to true, an effect navigates back.
 *
 * Self-recovers when network returns — the page automatically reloads
 * when the browser sees an online event again.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BrandedFaultRoute,
  FaultCtaRetry,
} from '../components/BrandedFaultRoute.tsx';

export function OfflineError() {
  const navigate = useNavigate();

  useEffect(() => {
    function onOnline(): void {
      // Browser reports we're back online. Reload to retry whatever was
      // happening pre-offline (don't just navigate back — components
      // mid-fetch may have crashed; a full reload is the cleanest reset).
      navigate('/home');
      window.location.reload();
    }
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, [navigate]);

  return (
    <BrandedFaultRoute
      kind="warning"
      iconKind="activity"
      eyebrow="No connection"
      title="Looks like you're offline."
      body="Check your internet connection and try again. We'll bring you back as soon as you're online."
      actions={<FaultCtaRetry />}
    />
  );
}
